import { PropertyAmenityFormRow } from '../types';

const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

/** Today's date in mm/dd/yyyy. */
export const todayDisplayDate = (): string => {
  const now = new Date();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  return `${month}/${day}/${now.getFullYear()}`;
};

/** Parse mm/dd/yyyy into a local Date; null when malformed. */
export const parseDisplayDate = (value: string): Date | null => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((value || '').trim());
  if (!match) return null;
  const date = new Date(Number(match[3]), Number(match[1]) - 1, Number(match[2]));
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Straight-line remaining value: cost × (1 − elapsed years ÷ depreciation years),
 * clamped to [0, cost] and rounded to cents. A future or invalid start date keeps full cost.
 */
export const calculateRemainingValue = (
  cost: number,
  serviceStartDate: string,
  depreciationYears: number,
  asOf: Date = new Date()
): number => {
  const safeCost = Number(cost) || 0;
  const start = parseDisplayDate(serviceStartDate);
  if (!start || !(depreciationYears > 0)) return safeCost;

  const elapsedYears = (asOf.getTime() - start.getTime()) / MS_PER_YEAR;
  if (elapsedYears <= 0) return safeCost;

  const remaining = safeCost * (1 - elapsedYears / depreciationYears);
  return Math.round(Math.min(safeCost, Math.max(0, remaining)) * 100) / 100;
};

export interface PropertyAmenityListItem {
  amenity: PropertyAmenityFormRow;
  /** Index in the full unsaved propertiesAmenities array */
  sourceIndex: number;
}

const typeSortName = (amenity: PropertyAmenityFormRow): string =>
  (amenity.typeName || `Type #${amenity.typeId}`).toLocaleLowerCase();

const dateSortValue = (value: string): number => parseDisplayDate(value)?.getTime() ?? 0;

/** Sort by type name ascending, then newest service date; preserve source indexes for edit/delete. */
export const getVisiblePropertyAmenities = (amenities: PropertyAmenityFormRow[]): PropertyAmenityListItem[] =>
  amenities
    .map((amenity, sourceIndex) => ({ amenity, sourceIndex }))
    .sort((a, b) => {
      const byType = typeSortName(a.amenity).localeCompare(typeSortName(b.amenity));
      if (byType !== 0) return byType;
      const byDate = dateSortValue(b.amenity.serviceStartDate) - dateSortValue(a.amenity.serviceStartDate);
      if (byDate !== 0) return byDate;
      return (b.amenity.id || 0) - (a.amenity.id || 0);
    });
